// Zooming a time axis (prototype charts: wheel and pinch zoom around the pointer, a dragged span, panning).
// Pure, so the limits are testable: the view never leaves the full range and never gets narrower than minSpan.

export interface Span {
  from: number;
  to: number;
}

/** The span scaled by `factor` (< 1 zooms in) around `at`, which stays under the pointer. */
export function zoomAt(
  view: Span,
  full: Span,
  at: number,
  factor: number,
  minSpan: number,
): Span {
  const fullLen = full.to - full.from;
  const len = Math.min(
    fullLen,
    Math.max(minSpan, (view.to - view.from) * factor),
  );
  const share =
    view.to > view.from ? (at - view.from) / (view.to - view.from) : 0.5;
  return clamp({ from: at - len * share, to: at - len * share + len }, full);
}

/** The span moved by `delta` ms, stopped at the ends of the full range. */
export function pan(view: Span, full: Span, delta: number): Span {
  return clamp({ from: view.from + delta, to: view.to + delta }, full);
}

/** A dragged selection as the new view, ignoring drags shorter than minSpan (a click, not a selection). */
export function select(
  a: number,
  b: number,
  full: Span,
  minSpan: number,
): Span | null {
  const from = Math.max(full.from, Math.min(a, b));
  const to = Math.min(full.to, Math.max(a, b));
  return to - from >= minSpan ? { from, to } : null;
}

/** Whether a view is the whole range (then the chart shows no zoom controls). */
export function isFull(view: Span, full: Span): boolean {
  return view.from <= full.from && view.to >= full.to;
}

function clamp(s: Span, full: Span): Span {
  const len = Math.min(s.to - s.from, full.to - full.from);
  const from = Math.min(Math.max(s.from, full.from), full.to - len);
  return { from, to: from + len };
}
